import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { Snackbar, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTourLauncher } from "../../contexts/context-tour";
import HelpOutline from '@mui/icons-material/HelpOutline';
import AppBar from '@mui/material/AppBar';
import {
  Avatar, Button, Box, IconButton, 
  Menu, MenuItem,
  Toolbar, Typography
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Leaderboard from '@mui/icons-material/Leaderboard';
import MenuIcon from '@mui/icons-material/Menu';
import { AppHelper } from '../../AppHelper';
const configJson = require('../../config.json');

const AppTopBar2 = forwardRef((props, ref)=> {
  const { drawerWidth, isClosing, mobileOpenClick, hideAttr, printMode, isEssayHome, isExerciseHome } = props;
  const navigate = useNavigate();
  const [authOK, setAuthOK] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [ oUser, setOUser] = React.useState(null);
  const [isCenter, setIsCenter] = React.useState(false);
  const [isTutor, setIsTutor] = React.useState(false);
  const [openSnackBar, setOpenSnackBar] = React.useState(false);
  const { launch } = useTourLauncher();
  const location = useLocation();

  //Pages that have tour guide should be add here so the help-tour-btn will show
  const pagesWithTour = [
    '/home-essay',
  ];

  const shouldShowTourButton = pagesWithTour.some(page => 
    location.pathname.startsWith(page)
  );

  useEffect(() => {
    const cached = AppHelper.userCacheGet();
    setAuthOK(!!cached);

    if (cached) {
      refreshAfterDeployment();   // keep your version check
      syncUserFromServer(cached); // always sync from server
    } else {
      setOUser(null);
      setIsCenter(false);
      setIsTutor(false);
    }
  }, []);

  const getAvatarUrl = (user) => {
    if (!user) return '/profile_icon/image_0_0.png';

    // New avatar system: use metadata filename first
    if (user.avatarImage) {
      return `/profile_icon/${user.avatarImage}`;
    }

    // For safety: if picture is already a filename like "image_0_1.png"
    if (user.picture && user.picture.endsWith('.png')) {
      return `/profile_icon/${user.picture}`;
    }

    // Legacy uploaded photos (base64 / jpeg path) are no longer supported
    // → fall back to a default icon instead of showing a broken grey avatar.
    return '/profile_icon/image_0_0.png';
  };

  const syncUserFromServer = async (cachedUser) => {
    if (!cachedUser || !cachedUser.token) {
      setOUser(cachedUser);
      return;
    }
    try {
      const res = await AppHelper.apiPost('api/profile/get', {
        token: cachedUser.token,
      });

      if (res && res.status) {
        const serverProfileId = res.data && res.data.profileId;
        if (serverProfileId && serverProfileId !== cachedUser.profileId) {
          setOUser(cachedUser);
          setIsCenter(cachedUser.accountType === 'TUI');
          setIsTutor(cachedUser.accountType === 'TEA');
          return;
        }
        if (res.avatarImage) {
          const updatedUser = {
            ...cachedUser,
            accountType: res.accountType || cachedUser.accountType,
            avatarImage: res.avatarImage,
            picture: res.avatarImage,
          };

          AppHelper.userCacheUpdate(updatedUser);
          setOUser(updatedUser);
          setIsCenter(updatedUser.accountType === 'TUI');
          setIsTutor(updatedUser.accountType === 'TEA');
          return;
        }
      }
    } catch (err) {
    }

    setOUser(cachedUser);
    if (cachedUser) {
      setIsCenter(cachedUser.accountType === 'TUI');
      setIsTutor(cachedUser.accountType === 'TEA');
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => { setAnchorEl(null) };
  const go2Home = () => {
    navigate('/home');
  }
  const go2Login = () => {
    navigate('/login');
  }
  const go2Profile = () => {
    navigate('/profile');
  }
  const go2Tutors = () => {
    navigate('/tutor');
  }
  const go2Students = () => {
    navigate('/student');
  }
  const go2Questions = () => {
    navigate('/question');
  }
  const logMeOut = async() =>{
    if(!oUser){
      alert('No or invalid user!');
      return;
    }
    if (window.confirm("Are you sure you want to log out? ") == false) {
      return;
    }
    AppHelper.apiAuditPost("P-SO", "");
    AppHelper.userCacheSet();
    go2Login();
  }
  const refreshProfile = () => {
    const cached = AppHelper.userCacheGet();
    setAuthOK(!!cached);

    if (cached) {
      setOUser(cached);
      setIsCenter(cached.accountType === 'TUI');
      setIsTutor(cached.accountType === 'TEA');
      syncUserFromServer(cached);
    } else {
      setOUser(null);
      setIsCenter(false);
      setIsTutor(false);
    }
  };

  const refreshAfterDeployment = async() => {
    var _res = await AppHelper.apiPost('api/app/version', {});
    if (!_res || !_res.status) {
      return;
    }
    const locAppVer = AppHelper.localAppVersion();
    const updatedAppVer = _res.data.version
    if (locAppVer == null) {
      AppHelper.localAppVersionSet(updatedAppVer);
    } 
    else if (locAppVer != updatedAppVer) 
    {
      setOpenSnackBar(true);
      AppHelper.localAppVersionSet(updatedAppVer);
      setTimeout(() => {
        window.location.reload(true);
      }, 3000);
    } 
    else if (locAppVer == updatedAppVer) 
    {}
  }
  const handleCloseSnackbar = () => {
    setOpenSnackBar(false);
  }
  const handleDrawerToggle = () => {
    if (!isClosing) {
      if(mobileOpenClick)
        mobileOpenClick();
    }
  };
  useImperativeHandle(ref, () => ({
    refreshProfile
  }));
  
  const isMobile = useMediaQuery('(max-width:600px)'); // Detect small screens

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{
          backgroundColor:'rgba(19,130,117,1)'
        }}>
          { oUser && !hideAttr &&
          <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
            {isEssayHome && 
              <MenuIcon />
            }
          </IconButton>
          }
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            <Box onClick={go2Home} component='a' sx={{cursor:'pointer', fontWeight:'bold', fontSize:'1.8rem'}}>
            EssayAce {configJson.mode!=='PROD'?'*DEV':''}
            </Box>
          </Typography>
          { authOK && !hideAttr &&
              <div>
                {shouldShowTourButton && (
                  <IconButton 
                    id="help-tour-btn"
                    aria-label="help"
                    color="inherit"
                    onClick={launch}
                    title="Open Tutorial"
                    sx={{ marginRight: 1 }}
                  >
                    <HelpOutline />
                  </IconButton>
                )}
          
                {isEssayHome && (
					isMobile ? (
					  <IconButton
            id="report-card-btn"
						size="large"
						aria-label="report card"
						href="/report"
						target="_blank"
						color="inherit"
					  >
						<Leaderboard />
					  </IconButton>
					) : (
					  <Button
            id="report-card-btn"
						startIcon={<Leaderboard />}
						href="/report"
						target="_blank"
						className="app-btn-bg-white"
						variant="contained"
					  >
						Report Card
					  </Button>
					)
				  )}
          
                <IconButton
                  id="menu-appbar"
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  {(!oUser) ? (
                    <AccountCircle fontSize='large' />
                  ) : (
                    <Avatar
                      src={getAvatarUrl(oUser)}
                      referrerPolicy="no-referrer" 
                    />
                  )}
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={go2Home}>Home</MenuItem>
                  { isCenter &&
                    <MenuItem onClick={go2Tutors}>Tutors</MenuItem>
                  }
                  
                  { (isCenter || isTutor) &&
                    <MenuItem onClick={go2Students}>Students</MenuItem>
                  }
                  <MenuItem onClick={go2Questions}>Questions</MenuItem>
                 
                  <MenuItem onClick={go2Profile}>Profile</MenuItem>
                  <MenuItem onClick={logMeOut}>Logout</MenuItem>
                </Menu>
              </div>
          }
          { (!authOK || hideAttr) && !printMode &&
            <>
            <Button href="https://app.essayace.ai" target="_blank" className="app-btn-bg-white" variant="contained">Try for Free</Button>
            </>
          }
        </Toolbar>
      </AppBar>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'center' }}
        open={openSnackBar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="New release. Refreshing... "
      />
    </Box>
  );
});
export default AppTopBar2;